<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import type { DeviceActionRequest } from '@atum-remote/protocol/device-actions';
import type {
  ConnectionTool,
  RemoteDevice,
  RemoteUiAdapter,
  SavedConnectionInput,
} from './model.js';
import {
  clearCustomization,
  readCustomizations,
  readDirectoryPreferences,
  renameGroup as renameGroupIn,
  writeCustomization,
  writeDirectoryPreferences,
  type DeviceCustomization,
  type DirectoryPreferences,
} from './lib/customization.js';
import { personalize, type DirectoryDevice } from './lib/directory.js';
import { initials, pluralize } from './lib/format.js';
import {
  parseRoute,
  routeHash,
  type DeviceTab,
  type Page,
  type Route,
  type SettingsTab,
} from './lib/route.js';
import { safeStorage } from './lib/storage.js';
import { useTheme } from './lib/use-theme.js';
import { useNativeUpdates } from './lib/native-updates.js';
import { useAccountPreferences } from './lib/account-preferences.js';
import { readThemePreference, resolveTheme } from './lib/theme.js';
import { customizationSchema, defaultPreferences, terminalSchema } from '@atum-remote/preferences';
import AddDeviceDialog from './components/AddDeviceDialog.vue';
import AppIcon from './components/AppIcon.vue';
import BrandMark from './components/BrandMark.vue';
import ConfirmDialog from './components/ConfirmDialog.vue';
import CustomizeDeviceDialog from './components/CustomizeDeviceDialog.vue';
import DeviceActionDialog from './components/DeviceActionDialog.vue';
import DevicePage from './components/DevicePage.vue';
import DevicesDirectory from './components/DevicesDirectory.vue';
import EmptyState from './components/EmptyState.vue';
import DownloadDialog from './components/DownloadDialog.vue';
import NyxWorkPage from './components/NyxWorkPage.vue';
import PeoplePage from './components/PeoplePage.vue';
import SettingsPage from './components/SettingsPage.vue';
import TextPromptDialog from './components/TextPromptDialog.vue';
import type { IconName } from './components/icons.js';

const props = defineProps<{ adapter: RemoteUiAdapter; combinedApp?: boolean }>();
const nativeUpdates = useNativeUpdates(props.adapter.updater);
// Hosts that gate on sign-in swap this app out when the session ends or the person signs out.
const emit = defineEmits<{ signedOut: [reason: 'expired' | 'signed-out'] }>();
const allowLocal = props.adapter.kind === 'desktop';
const storage = safeStorage();
const accountPreferences = useAccountPreferences();
const { preference: theme } = useTheme();

// Navigation is a hash route so back, refresh and deep links work everywhere.
const route = ref<Route>(parseRoute(window.location.hash, allowLocal));
const page = computed<Page>(() => route.value.page);

const devices = ref<readonly RemoteDevice[]>([]);
const loaded = ref(false);
const busy = ref(false);
const opening = ref(false);
const loadError = ref('');
const actionError = ref('');
const notice = ref('');
const refreshedAt = ref<number>();
const accountName = ref(props.adapter.accountName);
const accountAccess = ref(props.adapter.accountAccess);
const signInConfigured = ref(props.adapter.signInConfigured);
const connected = computed(() => accountName.value !== undefined);
// Owners retire registered devices; the installed app forgets saved SSH entries.
const canRemoveDevices = computed(
  () => props.adapter.supportsSavedConnections || accountAccess.value === 'owner',
);
const removable = (device: RemoteDevice) =>
  device.source === 'saved' || (canRemoveDevices.value && !device.personalAccess);
// The account block shows the name the person chose, with the identity from ATUM Auth beneath.
const displayName = computed(
  () => accountPreferences?.state.value.settings.account.displayName || accountName.value,
);
const avatarColor = computed(
  () => accountPreferences?.state.value.settings.account.avatarColor ?? 'mint',
);

// Appearance is one switch on the sidebar: light or dark. "System" resolves to whichever applies.
const lightQuery = window.matchMedia?.('(prefers-color-scheme: light)');
const systemLight = ref(lightQuery?.matches ?? false);
const onSystemTheme = (event: MediaQueryListEvent) => {
  systemLight.value = event.matches;
};
const light = computed(() => resolveTheme(theme.value, systemLight.value) === 'light');
function toggleTheme() {
  theme.value = light.value ? 'dark' : 'light';
}
const signedOut = computed(() => props.adapter.supportsSignIn && !connected.value);

// What the adapter reports, overlaid with what the person calls things.
const localCustomizations = ref(readCustomizations(storage));
const localDirectoryPrefs = ref<DirectoryPreferences>(readDirectoryPreferences(storage));
const customizations = computed(
  () => accountPreferences?.state.value.customizations ?? localCustomizations.value,
);
const directoryPrefs = computed(
  () => accountPreferences?.state.value.settings.directory ?? localDirectoryPrefs.value,
);
const preferencesError = computed(() => accountPreferences?.error.value ?? '');
const preferencesBusy = computed(
  () => !!accountPreferences && (!accountPreferences.ready.value || accountPreferences.busy.value),
);
const canImport = computed(
  () =>
    !!accountPreferences?.ready.value &&
    accountPreferences.state.value.revision === 0 &&
    loaded.value &&
    !loadError.value,
);
async function importBrowserSettings() {
  if (!accountPreferences || !canImport.value) return;
  const allowed = new Set(devices.value.map((device) => device.id));
  const settings = defaultPreferences().settings;
  settings.theme = readThemePreference(storage);
  settings.directory = {
    ...readDirectoryPreferences(storage),
    collapsed: readDirectoryPreferences(storage)
      .collapsed.filter((group) => group.trim() && group.length <= 60)
      .map((group) => group.trim()),
  };
  try {
    const terminal = terminalSchema
      .partial()
      .safeParse(JSON.parse(storage?.getItem('atum-remote.terminal.v1') ?? '{}'));
    if (terminal.success)
      settings.terminal = {
        fontSize: terminal.data.fontSize ?? 15,
        keyBar: terminal.data.keyBar ?? null,
      };
  } catch {
    /* Invalid legacy display preferences use defaults. */
  }
  try {
    await accountPreferences.update({
      type: 'import',
      settings,
      customizations: Object.fromEntries(
        Object.entries(readCustomizations(storage)).filter(([id]) => allowed.has(id)),
      ),
    });
    announce('Browser settings saved to your account.');
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Import failed.';
  }
}
const directory = computed<readonly DirectoryDevice[]>(() =>
  devices.value.map((device) => personalize(device, customizations.value[device.id])),
);
const groups = computed(() =>
  [...new Set(directory.value.map((device) => device.group))].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  ),
);
const selected = computed(() =>
  !signedOut.value && route.value.deviceId
    ? directory.value.find((device) => device.id === route.value.deviceId)
    : undefined,
);
const onlineCount = computed(
  () => devices.value.filter((device) => device.presence === 'online').length,
);
// Saved SSH connections are never probed, so an online count would be a false zero.
const reportsPresence = computed(() =>
  devices.value.some((device) => device.presence !== 'unchecked'),
);

const addDialog = ref<InstanceType<typeof AddDeviceDialog>>();
const customizeDialog = ref<InstanceType<typeof CustomizeDeviceDialog>>();
const confirmDialog = ref<InstanceType<typeof ConfirmDialog>>();
const deviceActionDialog = ref<InstanceType<typeof DeviceActionDialog>>();
const deviceActionBusy = ref(false);
watch(
  () => selected.value?.id,
  () => {
    if (!deviceActionBusy.value) deviceActionDialog.value?.close();
  },
);
async function requestDeviceAction(device: DirectoryDevice, action: DeviceActionRequest['action']) {
  if (deviceActionBusy.value || !props.adapter.runDeviceAction || !device.terminal.available)
    return;
  dialogOpen.value = true;
  await deviceActionDialog.value?.open(device, action);
}
async function confirmDeviceAction(device: DirectoryDevice, action: DeviceActionRequest['action']) {
  if (deviceActionBusy.value || selected.value?.id !== device.id || !props.adapter.runDeviceAction)
    return;
  deviceActionBusy.value = true;
  actionError.value = '';
  try {
    const result = await props.adapter.runDeviceAction(device, { action });
    const label = { lock: 'Lock', restart: 'Restart', shutdown: 'Shut down' }[action];
    if (result.status === 'accepted') announce(`${label} request accepted by ${device.name}.`);
    else if (result.status === 'rejected')
      actionError.value = `${device.name} did not accept the ${label.toLowerCase()} request. Check its session and system permissions.`;
    else
      actionError.value = `${label} was requested for ${device.name}, but acknowledgment was lost. Check the device before making another request.`;
  } catch (cause) {
    actionError.value =
      cause instanceof Error ? cause.message : 'The device action could not be started.';
  } finally {
    deviceActionBusy.value = false;
    deviceActionDialog.value?.close();
    void refresh();
  }
}
const promptDialog = ref<InstanceType<typeof TextPromptDialog>>();
const dialogOpen = ref(false);
const downloadDialog = ref<InstanceType<typeof DownloadDialog>>();
function showDownloads() {
  dialogOpen.value = true;
  void downloadDialog.value?.open();
}
let renamingGroup = '';

interface NavItem {
  readonly id: Page;
  readonly label: string;
  readonly icon: IconName;
}
const nav = computed<readonly NavItem[]>(() => [
  { id: 'devices', label: 'Devices', icon: 'devices' },
  { id: 'people', label: 'People & access', icon: 'people' },
  ...(allowLocal ? [{ id: 'local', label: 'This device', icon: 'laptop' } as const] : []),
  { id: 'settings', label: 'Settings', icon: 'settings' },
]);
const pageLabel = computed(
  () => nav.value.find((item) => item.id === page.value)?.label ?? 'Devices',
);
const title = computed(() =>
  selected.value ? selected.value.name : route.value.deviceId ? 'Device' : pageLabel.value,
);
const subtitle = computed(() => {
  if (route.value.deviceId) return '';
  switch (page.value) {
    case 'devices':
      return loaded.value && devices.value.length
        ? reportsPresence.value
          ? `${pluralize(devices.value.length, 'device')} · ${onlineCount.value} online · ${pluralize(groups.value.length, 'group')}`
          : `${pluralize(devices.value.length, 'saved connection')} · ${pluralize(groups.value.length, 'group')}`
        : 'Choose a device to open its desktop or terminal.';
    case 'people':
      return 'Share specific devices with people you trust.';
    case 'local':
      return 'The diagnostic agent, consent and startup on this computer.';
    default:
      return props.adapter.kind === 'web'
        ? 'Your account, the device directory and terminal text.'
        : 'The device directory, connections and updates on this computer.';
  }
});
const refreshedLabel = computed(() =>
  refreshedAt.value === undefined
    ? ''
    : new Date(refreshedAt.value).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      }),
);
const accountInitials = computed(() => (displayName.value ? initials(displayName.value) : ''));
const scope = props.adapter.kind === 'desktop' ? 'this computer' : 'your account';

let refreshController: AbortController | undefined;
let revision = 0;
let noticeTimer: ReturnType<typeof setTimeout> | undefined;
let refreshTimer: ReturnType<typeof setInterval> | undefined;

function syncRoute() {
  route.value = parseRoute(window.location.hash, allowLocal);
}
function go(next: Route) {
  notice.value = '';
  actionError.value = '';
  route.value = next;
  const hash = routeHash(next);
  if (window.location.hash !== hash) window.location.hash = hash;
}
function showDevice(id: string, tab?: DeviceTab) {
  go(
    tab && tab !== 'overview'
      ? { page: 'devices', deviceId: id, tab }
      : { page: 'devices', deviceId: id },
  );
}
function announce(message: string) {
  clearTimeout(noticeTimer);
  notice.value = message;
  noticeTimer = setTimeout(() => {
    notice.value = '';
  }, 8000);
}

async function refresh(background = false) {
  refreshController?.abort();
  const controller = new AbortController();
  refreshController = controller;
  const current = ++revision;
  busy.value = true;
  if (!background) loadError.value = '';
  try {
    const snapshot = await props.adapter.load(controller.signal);
    if (current !== revision) return;
    devices.value = snapshot.devices;
    loadError.value = '';
    refreshedAt.value = Date.now();
  } catch (cause) {
    if (current !== revision) return;
    // A background refresh keeps the last good list; an explicit one shows the failure plainly.
    if (!background || (props.adapter.supportsSignIn && props.adapter.accountName === undefined))
      devices.value = [];
    loadError.value =
      cause instanceof Error ? cause.message : 'Devices could not be loaded. Try refreshing.';
  } finally {
    if (current === revision) {
      busy.value = false;
      loaded.value = true;
      accountName.value = props.adapter.accountName;
      accountAccess.value = props.adapter.accountAccess;
      signInConfigured.value = props.adapter.signInConfigured;
      if (props.adapter.supportsSignIn && accountName.value === undefined)
        emit('signedOut', 'expired');
    }
  }
}
function onVisibility() {
  if (document.hidden || busy.value || dialogOpen.value || page.value !== 'devices') return;
  if (refreshedAt.value === undefined || Date.now() - refreshedAt.value > 30_000)
    void refresh(true);
}

// Personalization.
let editingRevision: number | undefined;
async function setPrefs(patch: Partial<DirectoryPreferences>) {
  try {
    if (accountPreferences) {
      const { collapsed, ...rest } = patch;
      await accountPreferences.update({
        type: 'settings',
        patch: { directory: { ...rest, ...(collapsed ? { collapsed: [...collapsed] } : {}) } },
      });
    } else localDirectoryPrefs.value = writeDirectoryPreferences(storage, patch);
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Preferences could not be saved.';
  }
}
function toggleGroup(name: string) {
  const collapsed = directoryPrefs.value.collapsed.includes(name)
    ? directoryPrefs.value.collapsed.filter((item) => item !== name)
    : [...directoryPrefs.value.collapsed, name];
  setPrefs({ collapsed });
}
async function customize(device: DirectoryDevice) {
  if (preferencesBusy.value) return;
  editingRevision = accountPreferences?.state.value.revision;
  actionError.value = '';
  dialogOpen.value = true;
  await customizeDialog.value?.open(device);
}
async function saveCustomization(
  id: string,
  patch: { [K in keyof DeviceCustomization]?: DeviceCustomization[K] | '' },
) {
  actionError.value = '';
  try {
    if (accountPreferences) {
      const value = customizationSchema.parse(
        Object.fromEntries(
          Object.entries({ ...customizations.value[id], ...patch }).filter(
            ([, value]) => value !== '' && value !== undefined,
          ),
        ),
      );
      await accountPreferences.update({ type: 'device', deviceId: id, value }, editingRevision);
    } else localCustomizations.value = writeCustomization(storage, id, patch);
    customizeDialog.value?.close();
    announce(`Device updated for ${scope}.`);
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'The device could not be updated.';
  }
}
async function resetCustomization(id: string) {
  actionError.value = '';
  try {
    if (accountPreferences)
      await accountPreferences.update({ type: 'reset-device', deviceId: id }, editingRevision);
    else localCustomizations.value = clearCustomization(storage, id);
    customizeDialog.value?.close();
    announce('Device reset to what it reports.');
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'The device could not be reset.';
  }
}
async function renameGroup(name: string) {
  editingRevision = accountPreferences?.state.value.revision;
  actionError.value = '';
  renamingGroup = name;
  dialogOpen.value = true;
  await promptDialog.value?.open({
    title: 'Rename group',
    label: 'Group name',
    value: name,
    confirmLabel: 'Rename',
    help: `Every device in ${name} moves to the new name for ${scope}.`,
  });
}
async function applyGroupRename(value: string) {
  actionError.value = '';
  const target = value.trim().replace(/\s+/g, ' ');
  try {
    if (accountPreferences)
      await accountPreferences.update(
        { type: 'rename-group', from: renamingGroup, to: target },
        editingRevision,
      );
    else localCustomizations.value = renameGroupIn(storage, renamingGroup, target, devices.value);
    if (!accountPreferences && directoryPrefs.value.collapsed.includes(renamingGroup))
      await setPrefs({
        collapsed: directoryPrefs.value.collapsed.map((item) =>
          item === renamingGroup ? target : item,
        ),
      });
    promptDialog.value?.close();
    announce(`Group renamed to ${target}.`);
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'The group could not be renamed.';
  }
}

// Saved connections (installed app).
async function showAdd() {
  actionError.value = '';
  dialogOpen.value = true;
  await addDialog.value?.open();
}
async function save(input: SavedConnectionInput) {
  busy.value = true;
  actionError.value = '';
  try {
    await props.adapter.saveConnection(input);
    addDialog.value?.close();
    announce(`${input.name} saved.`);
    await refresh();
  } catch (cause) {
    actionError.value =
      cause instanceof Error ? cause.message : 'The connection could not be saved.';
  } finally {
    busy.value = false;
  }
}
let removing: RemoteDevice | undefined;
async function askRemove(device: RemoteDevice) {
  if (!removable(device) || busy.value) return;
  removing = device;
  actionError.value = '';
  dialogOpen.value = true;
  await confirmDialog.value?.open(
    device.source === 'saved'
      ? {
          title: `Remove ${device.name}?`,
          message: 'The saved connection is forgotten in this app.',
          detail: 'SSH access on the device itself stays in place.',
          confirmLabel: 'Remove',
          busyLabel: 'Removing…',
          danger: true,
        }
      : {
          title: `Remove ${device.name}?`,
          message:
            'The device is retired for your whole account. Open sessions end and it can no longer connect.',
          detail: 'To use it again, register it with the server once more.',
          confirmLabel: 'Remove device',
          busyLabel: 'Removing…',
          danger: true,
        },
  );
}
async function remove(device: RemoteDevice | undefined = removing) {
  if (!device) return;
  busy.value = true;
  actionError.value = '';
  try {
    await props.adapter.removeConnection(device.id);
    confirmDialog.value?.close();
    go({ page: 'devices' });
    // Removal has already succeeded even if optional label cleanup fails.
    try {
      if (!accountPreferences) localCustomizations.value = clearCustomization(storage, device.id);
    } catch (cause) {
      actionError.value =
        cause instanceof Error ? cause.message : 'Saved labels could not be cleared.';
    }
    announce(
      device.source === 'saved'
        ? `${device.name} removed from this app.`
        : `${device.name} removed from your account.`,
    );
    await refresh();
  } catch (cause) {
    actionError.value =
      cause instanceof Error && device.source !== 'saved'
        ? cause.message
        : 'The device could not be removed.';
  } finally {
    busy.value = false;
  }
}

// Account and sessions.
function signIn() {
  if (signInConfigured.value) props.adapter.signIn();
}
async function signOut() {
  refreshController?.abort();
  revision++;
  busy.value = true;
  actionError.value = '';
  try {
    await props.adapter.signOut();
    accountName.value = undefined;
    devices.value = [];
    loadError.value = '';
    go({ page: 'devices' });
    emit('signedOut', 'signed-out');
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Sign out failed. Try again.';
  } finally {
    busy.value = false;
  }
}
async function open(device: DirectoryDevice, tool: ConnectionTool) {
  if (!device[tool]?.available || opening.value) return;
  opening.value = true;
  actionError.value = '';
  try {
    await props.adapter.openConnection(device, tool);
    if (tool === 'nyx' && props.adapter.kind === 'desktop') return;
    const label = tool === 'nyx' ? 'AI work' : tool;
    announce(
      props.adapter.kind === 'desktop'
        ? `${device.name} opened in its own ${label} window. Connection status and Disconnect are in that window.`
        : `Opening ${device.name} ${label}…`,
    );
  } catch (cause) {
    actionError.value =
      cause instanceof Error ? cause.message : 'The connection could not be opened.';
  } finally {
    opening.value = false;
  }
}

onMounted(() => {
  if (nativeUpdates.state.phase === 'idle') void nativeUpdates.check();
  window.addEventListener('hashchange', syncRoute);
  document.addEventListener('visibilitychange', onVisibility);
  lightQuery?.addEventListener('change', onSystemTheme);
  refreshTimer = setInterval(() => {
    if (
      !document.hidden &&
      !busy.value &&
      !dialogOpen.value &&
      connected.value &&
      page.value === 'devices'
    )
      void refresh(true);
  }, 60_000);
  void refresh();
});
onUnmounted(() => {
  window.removeEventListener('hashchange', syncRoute);
  document.removeEventListener('visibilitychange', onVisibility);
  lightQuery?.removeEventListener('change', onSystemTheme);
  clearInterval(refreshTimer);
  clearTimeout(noticeTimer);
  refreshController?.abort();
});
</script>

<template>
  <NyxWorkPage
    v-if="selected && route.tab === 'nyx' && selected.hostProfile !== 'linux-server'"
    :key="selected.id"
    :device="selected"
    :adapter="props.adapter"
    @back="showDevice(selected.id)"
  />
  <div v-else class="app-shell" :data-kind="props.adapter.kind">
    <aside class="app-nav">
      <button
        type="button"
        class="brand"
        aria-label="ATUM Remote devices"
        @click="go({ page: 'devices' })"
      >
        <BrandMark /><span class="brand-text"><b>ATUM</b> Remote</span>
      </button>
      <nav aria-label="Main navigation">
        <button
          v-for="item in nav"
          :key="item.id"
          type="button"
          class="nav-item"
          :aria-current="page === item.id ? 'page' : undefined"
          @click="go({ page: item.id })"
        >
          <AppIcon :name="item.icon" /><span>{{
            item.id === 'settings' && nativeUpdates.state.phase === 'available'
              ? 'Update available'
              : item.label
          }}</span>
        </button>
        <button
          v-if="props.adapter.kind === 'web' && !props.combinedApp"
          type="button"
          class="nav-item"
          aria-haspopup="dialog"
          @click="showDownloads"
        >
          <AppIcon name="download" /><span>Download</span>
        </button>
      </nav>
      <div class="nav-footer">
        <button
          type="button"
          class="theme-toggle"
          role="switch"
          :aria-checked="light ? 'true' : 'false'"
          :disabled="preferencesBusy"
          :title="light ? 'Switch to dark theme' : 'Switch to light theme'"
          @click="toggleTheme"
        >
          <span class="theme-toggle-icon" aria-hidden="true">
            <AppIcon :name="light ? 'sun' : 'moon'" />
          </span>
          <span class="theme-toggle-text">{{ light ? 'Light' : 'Dark' }} theme</span>
          <span class="switch-track" aria-hidden="true"><span class="switch-thumb" /></span>
        </button>
        <button
          v-if="props.adapter.supportsSignIn"
          type="button"
          class="account"
          :data-signed-in="connected ? 'true' : 'false'"
          :title="connected ? 'Account settings' : 'Sign in from Settings'"
          @click="go({ page: 'settings' })"
        >
          <span class="avatar" :data-color="connected ? avatarColor : undefined" aria-hidden="true">
            <template v-if="connected">{{ accountInitials }}</template>
            <AppIcon v-else name="user" />
          </span>
          <span class="account-text">
            <strong>{{ connected ? displayName : 'Not signed in' }}</strong>
            <span>{{
              connected && displayName !== accountName ? accountName : 'ATUM account'
            }}</span>
          </span>
        </button>
        <a
          v-if="props.adapter.kind === 'web'"
          class="credit"
          href="https://atum.tech"
          rel="noreferrer"
          title="Made by ATUM"
        >
          <BrandMark /><span>Made by ATUM · atum.tech</span>
        </a>
        <span v-else class="credit" title="Made by ATUM">
          <BrandMark /><span>Made by ATUM · atum.tech</span>
        </span>
      </div>
    </aside>

    <main class="app-main">
      <header class="page-heading">
        <div class="page-heading-text">
          <button
            v-if="route.deviceId"
            type="button"
            class="back-link"
            @click="go({ page: 'devices' })"
          >
            <AppIcon name="back" />Devices
          </button>
          <p v-else class="eyebrow"><BrandMark class="mobile-mark" />ATUM Remote</p>
          <h1>{{ title }}</h1>
          <p v-if="subtitle" class="page-subtitle">{{ subtitle }}</p>
        </div>
        <button
          type="button"
          class="icon-button quiet theme-toggle-compact"
          :aria-label="light ? 'Switch to dark theme' : 'Switch to light theme'"
          :disabled="preferencesBusy"
          @click="toggleTheme"
        >
          <AppIcon :name="light ? 'sun' : 'moon'" />
        </button>
        <div v-if="page === 'devices' && !route.deviceId" class="actions">
          <button
            type="button"
            class="quiet"
            :disabled="busy"
            :aria-label="busy ? 'Refreshing devices' : 'Refresh devices'"
            @click="refresh()"
          >
            <AppIcon name="refresh" :class="{ spin: busy }" />
            <span class="action-label">{{ busy ? 'Refreshing…' : 'Refresh' }}</span>
          </button>
          <button
            v-if="props.adapter.supportsSavedConnections"
            type="button"
            class="primary"
            @click="showAdd"
          >
            <AppIcon name="plus" /><span>Add device</span>
          </button>
          <button
            v-else-if="signedOut && signInConfigured && loaded"
            type="button"
            class="primary"
            @click="signIn"
          >
            <AppIcon name="key" /><span>Sign in</span>
          </button>
        </div>
      </header>

      <div v-if="preferencesError" class="banner error" role="alert">
        <AppIcon name="warning" /><span>{{ preferencesError }}</span>
        <button
          type="button"
          :disabled="accountPreferences?.busy.value"
          @click="accountPreferences?.load().catch(() => {})"
        >
          Reload settings
        </button>
      </div>
      <div v-if="actionError && !dialogOpen" class="banner error" role="alert">
        <AppIcon name="warning" /><span>{{ actionError }}</span>
        <button
          type="button"
          class="icon-button quiet small"
          aria-label="Dismiss"
          @click="actionError = ''"
        >
          <AppIcon name="close" />
        </button>
      </div>
      <div v-if="notice" class="banner" role="status">
        <AppIcon name="check" /><span>{{ notice }}</span>
        <button
          type="button"
          class="icon-button quiet small"
          aria-label="Dismiss"
          @click="notice = ''"
        >
          <AppIcon name="close" />
        </button>
      </div>

      <template v-if="page === 'devices'">
        <div v-if="loadError && devices.length && !signedOut" class="banner error" role="alert">
          <AppIcon name="warning" />
          <span>Could not refresh: {{ loadError }} Showing the last known list.</span>
          <button type="button" class="small" :disabled="busy" @click="refresh()">Retry</button>
        </div>
        <DevicePage
          v-if="selected"
          :device="selected"
          :tab="
            selected.hostProfile === 'linux-server' && route.tab === 'nyx'
              ? 'overview'
              : (route.tab ?? 'overview')
          "
          :opening="opening"
          :busy="busy || deviceActionBusy"
          :preferences-busy="preferencesBusy"
          :adapter="props.adapter"
          :removable="removable(selected)"
          @open="open(selected, $event)"
          @customize="customize(selected)"
          @remove="askRemove(selected)"
          @device-action="requestDeviceAction(selected, $event)"
          @navigate="showDevice(selected.id, $event)"
        />
        <EmptyState
          v-else-if="route.deviceId && loaded && !busy && !signedOut && !loadError"
          title="This device is no longer listed"
          icon="devices"
        >
          It may have been removed, or your access changed. Refresh to see the current list.
          <template #actions>
            <button type="button" class="primary" @click="go({ page: 'devices' })">
              All devices
            </button>
            <button type="button" @click="refresh()">Refresh</button>
          </template>
        </EmptyState>
        <template v-else>
          <section v-if="!loaded" class="device-grid" aria-busy="true" aria-label="Loading devices">
            <div v-for="n in 3" :key="n" class="device-card skeleton-card" aria-hidden="true">
              <div class="skeleton glyph" />
              <div class="skeleton line w40" />
              <div class="skeleton line w70" />
              <div class="skeleton line w50" />
              <div class="skeleton button" />
            </div>
          </section>
          <EmptyState v-else-if="signedOut" brand title="Sign in to see your devices">
            Use your ATUM account to open your private device list. Sessions expire after 30 minutes
            of inactivity.
            <template #actions>
              <button
                type="button"
                class="primary"
                :disabled="busy || !signInConfigured"
                @click="signIn"
              >
                <AppIcon name="key" />Continue with ATUM Auth
              </button>
            </template>
            <template v-if="loadError || !signInConfigured" #hint>
              {{ loadError || 'Owner sign-in is not configured on this server yet.' }}
            </template>
          </EmptyState>
          <EmptyState
            v-else-if="loadError && !devices.length"
            title="Devices unavailable"
            icon="warning"
            tone="danger"
          >
            {{ loadError }}
            <template #actions>
              <button type="button" class="primary" :disabled="busy" @click="refresh()">
                Try again
              </button>
            </template>
          </EmptyState>
          <EmptyState
            v-else-if="!devices.length"
            :title="
              props.adapter.supportsSavedConnections ? 'Your devices go here' : 'No devices yet'
            "
            icon="devices"
          >
            <template v-if="props.adapter.supportsSavedConnections">
              Save an existing SSH connection to bring your devices together. Your SSH keys stay on
              this computer.
            </template>
            <template v-else>
              No devices are registered with this server yet. Enroll a device and grant it to your
              account, then refresh.
            </template>
            <template #actions>
              <button
                v-if="props.adapter.supportsSavedConnections"
                type="button"
                class="primary"
                @click="showAdd"
              >
                <AppIcon name="plus" />Add your first device
              </button>
              <button v-else type="button" :disabled="busy" @click="refresh()">Refresh</button>
            </template>
          </EmptyState>
          <template v-else>
            <DevicesDirectory
              :devices="directory"
              :opening="opening"
              :preferences-busy="preferencesBusy"
              :collapsed="directoryPrefs.collapsed"
              :refreshed-label="refreshedLabel"
              :view="directoryPrefs.view"
              :sort="directoryPrefs.sort"
              :removable="removable"
              @update:view="setPrefs({ view: $event })"
              @update:sort="setPrefs({ sort: $event })"
              @select="showDevice($event)"
              @open="open"
              @customize="customize"
              @remove="askRemove"
              @rename-group="renameGroup"
              @toggle-group="toggleGroup"
            />
          </template>
        </template>
      </template>

      <PeoplePage v-else-if="page === 'people'" />

      <section v-else-if="page === 'local'" class="page-stack">
        <slot name="this-device">
          <EmptyState title="Device status unavailable" icon="laptop">
            Open the installed app to manage this device.
          </EmptyState>
        </slot>
      </section>

      <SettingsPage
        v-else
        :adapter="props.adapter"
        :connected="connected"
        :account-name="accountName"
        :sign-in-configured="signInConfigured"
        :busy="busy"
        :preferences-busy="preferencesBusy"
        :can-import="canImport"
        :tab="route.settingsTab ?? 'account'"
        :directory="directoryPrefs"
        @sign-in="signIn"
        @sign-out="signOut"
        @import-browser-settings="importBrowserSettings"
        @navigate="go({ page: 'settings', settingsTab: $event as SettingsTab })"
        @update-directory="setPrefs($event)"
      />
    </main>

    <DownloadDialog
      v-if="props.adapter.kind === 'web' && !props.combinedApp"
      ref="downloadDialog"
      :open-external="props.adapter.openExternal"
      @close="dialogOpen = false"
    />
    <AddDeviceDialog
      v-if="props.adapter.supportsSavedConnections"
      ref="addDialog"
      :busy="busy"
      :error="dialogOpen ? actionError : ''"
      :groups="groups"
      @save="save"
      @close="dialogOpen = false"
    />
    <CustomizeDeviceDialog
      ref="customizeDialog"
      :busy="busy || !!accountPreferences?.busy.value"
      :save-disabled="!!accountPreferences && !accountPreferences.ready.value"
      :groups="groups"
      :scope="scope"
      :error="dialogOpen ? actionError : ''"
      @save="saveCustomization"
      @reset="resetCustomization"
      @close="dialogOpen = false"
    />
    <DeviceActionDialog
      ref="deviceActionDialog"
      :busy="deviceActionBusy"
      @confirm="confirmDeviceAction"
      @close="dialogOpen = false"
    />
    <TextPromptDialog
      ref="promptDialog"
      :busy="preferencesBusy"
      :error="dialogOpen ? actionError : ''"
      @confirm="applyGroupRename"
      @close="dialogOpen = false"
    />
    <ConfirmDialog
      ref="confirmDialog"
      :busy="busy"
      :error="dialogOpen ? actionError : ''"
      @confirm="remove()"
      @close="dialogOpen = false"
    />
  </div>
</template>
